import {
  ACCEPT_COMPANY_AGREEMENT,
  COMPANIES_DOCUMENTS,
  COMPANY_ATTORNEY,
  COMPANY_FEES,
  COMPANY_INVOICING_CONTACT,
  COMPANY_ONBOARDING_STATUSES,
  COMPANY_STATUSES,
  CORE_COMPANY,
  CREATE_COMPANY_ACCOUNT,
  CREATE_ERP_CONNECTION,
  DISCONNECT_BANKING_CONNECTION,
  DISCONNECT_ERP_CONNECTION,
  DOCUMENTS_REQUEST_SIGNATURE,
  DOCUMENTS_RESEND_EMAILS,
  DOES_COMPANY_EXIST,
  GET_ALL_CREDITS,
  GET_AVAILABLE_CREDITS_AMOUNT,
  IMPERSONATE_USER,
  REFRESH_BANKING_CONNECTION,
  REFRESH_ERP_CONNECTION,
  START_BANKING_CONNECTION,
  UPDATE_COMPANIES_DOCUMENTS,
  VALIDATE_KYC_DATA,
} from '@/constants/api';
import {
  DeactivateCompanyBankAccountMutationType,
  DeactivateCompanyBankAccountMutationVariablesType,
  deactivateCompanyBankAccountMutation,
} from '@/schemas/deactivateCompanyBankAccountMutation';
import {
  GetCompanyAgreementConsentVersionNumberType,
  getActiveCompanyAgreementConsentVersionNumber,
} from '@/schemas/getActiveCompanyAgreementConsentVersionNumber';
import {
  GetActiveCompanyBankAccountByIdQueryType,
  GetActiveCompanyBankAccountByIdQueryVariablesType,
  getActiveCompanyBankAccountByIdQuery,
} from '@/schemas/getActiveCompanyBankAccountById';
import {
  GetActiveUserTermsConsentsType,
  getActiveUserTermsConsentsQuery,
} from '@/schemas/getActiveUserTermsConsents';
import {
  GetAllFeatureFlagsQueryType,
  GetAllFeatureFlagsQueryVariables,
  getAllFeatureFlags,
} from '@/schemas/getAllFeatureFlags';
import { getAllPotentialCompanyUsersByIdQuery } from '@/schemas/getAllPotentialCompanyUsersByIdQuery';
import {
  GetBankingConnectionQueryType,
  GetBankingConnectionQueryVariablesType,
  getBankingConnectionQuery,
} from '@/schemas/getBankingConnectionQuery';
import {
  CompaniesPowerOfAttorneys,
  CompaniesPowerOfAttorneysVariables,
  getCompaniesPowerOfAttorneysQuery,
} from '@/schemas/getCompaniesPowerOfAttorneysQuery';
import { GetCompanyType, getCompanyQuery } from '@/schemas/getCompany';
import {
  GetCompanyAgreementConsentsType,
  getCompanyAgreementConsentsQuery,
} from '@/schemas/getCompanyAgreementConsents';
import {
  CompanyAttachments,
  getCompanyAttachments,
} from '@/schemas/getCompanyAttachments';
import {
  GetCompanyBankAccountByIdQueryType,
  GetCompanyBankAccountByIdQueryVariablesType,
  getCompanyBankAccountByIdQuery,
} from '@/schemas/getCompanyBankAccountById';
import {
  GetCompanyBankAccountsQueryType,
  getCompanyBankAccountsQuery,
} from '@/schemas/getCompanyBankAccounts';
import {
  GetCompanyBankingConnectionQueryType,
  GetCompanyBankingConnectionQueryVariablesType,
  getCompanyBankingConnectionsQuery,
} from '@/schemas/getCompanyBankingConnectionsQuery';
import {
  GetCompanyCoreSignatoriesType,
  getCompanyCoreSignatoriesQuery,
} from '@/schemas/getCompanyCoreSignatoriesQuery';
import {
  GetCompanyErpConnectionQueryType,
  GetCompanyErpConnectionQueryVariablesType,
  getCompanyErpConnectionsQuery,
} from '@/schemas/getCompanyErpConnectionQuery';
import { getCompanyInvoicingContactQuery } from '@/schemas/getCompanyInvoicingContactQuery';
import {
  GetCompanyLimitExpirationDateType,
  getCompanyLimitExpirationDateQuery,
} from '@/schemas/getCompanyLimitExpirationDateQuery';
import {
  CompanyOwners,
  CompanyOwnersVariables,
  getCompanyOwnersQuery,
} from '@/schemas/getCompanyOwners';
import {
  GetCompanyRiskKycQueryType,
  getCompanyRiskKycQuery,
} from '@/schemas/getCompanyRiskKycQuery';
import {
  SignAloneUsers,
  getCompanySignAloneUsersQuery,
} from '@/schemas/getCompanySignAloneUsers';
import {
  OrderCompanies,
  OrderCompaniesVariables,
  getCompanyUbosQuery,
} from '@/schemas/getCompanyUbosQuery';
import {
  GetCompanyUsersType,
  getCompanyUsersById,
} from '@/schemas/getCompanyUsersById';
import {
  GetCompanyVouchersType,
  getCompanyVouchersQuery,
} from '@/schemas/getCompanyVouchers';
import { getCoreCompanyMembersByCompanyIdQuery } from '@/schemas/getCoreCompanyMembersByCompanyIdQuery';
import {
  GetInvoiceCreditsQueryVariables,
  GetInvoiceCreditsTypes,
  getCreditAmountByInvoiceQuery,
} from '@/schemas/getCreditAmountByInvoiceQuery';
import {
  GetErpIntegrationQueryType,
  GetErpIntegrationQueryVariablesType,
  getErpIntegrationQuery,
} from '@/schemas/getErpIntegrationQuery';
import { getErpSystemQuery } from '@/schemas/getErpSystemQuery';
import { GetFeesQueryType, getFeesQuery } from '@/schemas/getFeesQuery';
import {
  GetImpersonateSearchResultsQueryType,
  getImpersonateSearchResultsQuery,
} from '@/schemas/getImpersonateSearchResultsQuery';
import { GetKycByIdType, getKycById } from '@/schemas/getKycById';
import { getLimitDeactivationReasonQuery } from '@/schemas/getLimitDeactivationReasonQuery';
import {
  GetOrderVouchersQueryVariables,
  GetOrderVouchersType,
  getOrderVouchersQuery,
} from '@/schemas/getOrderVouchersQuery';
import { getPendingCompanyUsersByIdQuery } from '@/schemas/getPendingCompanyUsersByIdQuery';
import {
  PowerOfAttorneys,
  getPowerOfAttorneys,
} from '@/schemas/getPowerOfAttorneys';
import {
  GetSignatoriesByCompanyIdType,
  getSignatoriesByCompanyId,
} from '@/schemas/getSignatoriesByCompanyId';
import { getUserTermsConsentsQuery } from '@/schemas/getUserTermsConsents';
import {
  SubmitCompanyKycMutationResponseType,
  submitCompanyKycMutation,
} from '@/schemas/submitCompanyKycMutation';
import {
  UpdateCompanyKycMutationResponseType,
  UpdateCompanyKycMutationVariablesType,
  updateCompanyKycMutation,
} from '@/schemas/updateCompanyKycMutation';
import {
  UpdateErpSystemMutationVariables,
  updateErpSystemMutation,
} from '@/schemas/updateErpSystemMutation';
import {
  AddCompanyForm,
  BankingConnectionResponse,
  CompanyData,
  CompanyExistsResponse,
  CompanyFees,
  CompanyInvoicingContactPayload,
  CompanyStatusesResponse,
  CoreCompanyResponseData,
  Documents,
  GetCompanyCreditsType,
  ImpersonateUserPayload,
  ImpersonateUserResponse,
} from '@/types/company';
import { ErpConnectionResponse } from '@/types/erp';
import { KycData, KycValidationResponse } from '@/types/kyc';
import { OnboardingStatuses } from '@/types/onboarding';

import { get, proxy, save } from '@/utils/request';
import { REQUEST_METHOD } from '@treyd-io/core/constants/request';
import { QueryOptions } from '@treyd-io/core/types/hasura';
import { sanitizeOrgNumber } from '@treyd-io/core/utils/organization';

export const getCompanyOnboardingStatuses = async (companyId: number) =>
  get<OnboardingStatuses>({ url: COMPANY_ONBOARDING_STATUSES(companyId) });

export namespace getCompanyOwners {
  export const client = async (companiesIds: number[], userId: number) => {
    return proxy<CompanyOwners, CompanyOwnersVariables>({
      query: getCompanyOwnersQuery,
      variables: { userId, companiesIds },
    });
  };
}
export const getCompaniesPowerOfAttorneys = async (
  companiesIds: number[],
  userId: number
) => {
  return proxy<CompaniesPowerOfAttorneys, CompaniesPowerOfAttorneysVariables>({
    query: getCompaniesPowerOfAttorneysQuery,
    variables: { companiesIds, userId },
  });
};

export const getCompanyUbos = async (companyId: number) => {
  return proxy<OrderCompanies, OrderCompaniesVariables>({
    query: getCompanyUbosQuery,
    variables: { company_id: companyId },
  });
};

export const getCompanySignatories = async (companyId: number) => {
  return proxy<GetSignatoriesByCompanyIdType, { company_id: number }>({
    query: getSignatoriesByCompanyId,
    variables: { company_id: companyId },
  });
};
export const getCompanySignAloneUsers = async (companyId: number) => {
  return proxy<SignAloneUsers, { company_id: number }>({
    query: getCompanySignAloneUsersQuery,
    variables: { company_id: companyId },
  });
};

export const getCompanyKyc = async (companyId: number) => {
  return proxy<GetKycByIdType, { companyId: number }>({
    query: getKycById,
    variables: { companyId },
  });
};

export const getCompanyRiskKyc = async (companyId: number) => {
  return proxy<GetCompanyRiskKycQueryType, { companyId: number }>({
    query: getCompanyRiskKycQuery,
    variables: { companyId },
  });
};

export const getCompanyUsers = async (companyId: number) => {
  return proxy<GetCompanyUsersType, { company_id: number }>({
    query: getCompanyUsersById,
    variables: { company_id: companyId },
  });
};

export const sendPOA = ({ companyId, signer_id, user_id }: Documents.Payload) =>
  save<{ payload: Documents.DocumentType }>({
    url: COMPANY_ATTORNEY(companyId),
    method: REQUEST_METHOD.POST,
    data: { signer_id, user_id },
  });

export const getCompanyDocuments = (companyId: number) =>
  get<Documents.DocumentResponse>({
    url: COMPANIES_DOCUMENTS(companyId),
  });

export const updateCompanyDocuments = ({
  companyId,
  documentId,
  data,
}: {
  companyId: number;
  documentId: number;
  data: Partial<Documents.DocumentType['data']>;
}) =>
  save<Documents.DocumentType>({
    url: UPDATE_COMPANIES_DOCUMENTS(companyId, documentId),
    method: REQUEST_METHOD.PUT,
    data: { data },
  });

export const requestDocumentSignature = ({
  companyId,
  documentId,
}: Documents.RequestSignaturePayload) =>
  save({
    url: DOCUMENTS_REQUEST_SIGNATURE('companies', companyId, documentId),
    method: REQUEST_METHOD.PUT,
  });

export const resendDocumentEmails = ({
  companyId,
  documentId,
}: Documents.ResendDocumentEmailsPayload) =>
  save({
    url: DOCUMENTS_RESEND_EMAILS('companies', companyId, documentId),
    method: REQUEST_METHOD.PUT,
  });

export const getPOA = async (companyId: number) => {
  return proxy<PowerOfAttorneys, { company_id: number }>({
    query: getPowerOfAttorneys,
    variables: { company_id: companyId },
  });
};

export const createCoreCompany = async (params: AddCompanyForm) =>
  save<CoreCompanyResponseData>({
    url: CORE_COMPANY(
      params.country,
      sanitizeOrgNumber(params.org_no).toUpperCase()
    ),
    method: REQUEST_METHOD.POST,
  });

export const createCompanyAccount = async (params: {
  country: string;
  org_no: string;
  industry?: string;
  website?: string;
  desired_limit?: number;
  desired_limit_currency?: string;
  main_contact_id?: number;
  hubspot_metadata?: Record<string, string>;
}) =>
  save<CompanyData>({
    url: CREATE_COMPANY_ACCOUNT,
    data: params,
    method: REQUEST_METHOD.POST,
  });

export const editCompanyInvoicingContact = async ({
  companyId,
  data,
}: {
  companyId: number;
  data: CompanyInvoicingContactPayload;
}) =>
  save<{
    invoicing_contact_updated: boolean;
  }>({
    url: COMPANY_INVOICING_CONTACT(companyId),
    data,
    method: REQUEST_METHOD.POST,
  });

export const getCompanyInvoicingContact = async (companyId: number) => {
  return proxy({
    query: getCompanyInvoicingContactQuery,
    variables: { companyId },
  });
};

export const doesCompanyExists = (params: {
  country: string;
  org_no: string;
}) =>
  save<CompanyExistsResponse>({
    url: DOES_COMPANY_EXIST,
    data: params,
    method: REQUEST_METHOD.POST,
  });

export const companyAttachments = async () => {
  return proxy<CompanyAttachments>({
    query: getCompanyAttachments,
  });
};

export const getCompanyAgreementConsents = async (
  companyId: number,
  versionNumber: number
) => {
  return proxy<
    GetCompanyAgreementConsentsType,
    { companyId: number; versionNumber: number }
  >({
    query: getCompanyAgreementConsentsQuery,
    variables: { companyId, versionNumber },
  });
};

export const getCompanyAgreementConsentVersionNumber = async (
  companyId: number
) => {
  return proxy<
    GetCompanyAgreementConsentVersionNumberType,
    { companyId: number }
  >({
    query: getActiveCompanyAgreementConsentVersionNumber,
    variables: { companyId },
  });
};

export const getUserTermsConsents = async (
  userId: number,
  versionNumber: number
) => {
  return proxy<
    GetCompanyAgreementConsentsType,
    { userId: number; versionNumber: number }
  >({
    query: getUserTermsConsentsQuery,
    variables: { userId, versionNumber },
  });
};

export const getActiveUserTermsConsents = async (userId: number) => {
  return proxy<GetActiveUserTermsConsentsType, { userId: number }>({
    query: getActiveUserTermsConsentsQuery,
    variables: { userId },
  });
};

export const getCompanyCoreSignatories = async (companyId: number) => {
  return proxy<GetCompanyCoreSignatoriesType, { companyId: number }>({
    query: getCompanyCoreSignatoriesQuery,
    variables: { companyId },
  });
};

export const getCompany = async (companyId: number) => {
  return proxy<GetCompanyType, { companyId: number }>({
    query: getCompanyQuery,
    variables: { companyId },
  });
};

export const getCompanyStatuses = async (companyId: number) =>
  get<CompanyStatusesResponse>({ url: COMPANY_STATUSES(companyId) });

export const updateCompanyKyc = async (
  options: UpdateCompanyKycMutationVariablesType
) => {
  return proxy<
    UpdateCompanyKycMutationResponseType,
    UpdateCompanyKycMutationVariablesType
  >({
    mutation: updateCompanyKycMutation,
    variables: options,
  });
};

export const submitCompanyKyc = async () => {
  return proxy<SubmitCompanyKycMutationResponseType>({
    mutation: submitCompanyKycMutation,
  });
};

export const acceptCompanyAgreement = () =>
  save({
    url: ACCEPT_COMPANY_AGREEMENT,
    method: REQUEST_METHOD.POST,
  });

export const updateErpSystem = async (
  payload: UpdateErpSystemMutationVariables
) => {
  return proxy({
    mutation: updateErpSystemMutation,
    variables: payload,
  });
};

export const getErpSystem = async (id: number) => {
  return proxy({
    query: getErpSystemQuery,
    variables: { id },
  });
};

export const getAllPotentialCompanyUsersById = async (companyId: number) => {
  return proxy({
    query: getAllPotentialCompanyUsersByIdQuery,
    variables: { companyId },
  });
};

export const getPendingCompanyUsersById = async (companyId?: number) => {
  return proxy({
    query: getPendingCompanyUsersByIdQuery,
    variables: { company_id: companyId },
  });
};

export const getCoreCompanyMembersByCompanyId = async (companyId: number) => {
  return proxy({
    query: getCoreCompanyMembersByCompanyIdQuery,
    variables: { companyId },
  });
};

export const getErpIntegration = async (
  payload: GetErpIntegrationQueryVariablesType
) => {
  return proxy<GetErpIntegrationQueryType, GetErpIntegrationQueryVariablesType>(
    {
      query: getErpIntegrationQuery,
      variables: payload,
    }
  );
};

export const getBankingConnection = async (
  payload: GetBankingConnectionQueryVariablesType
) => {
  return proxy<
    GetBankingConnectionQueryType,
    GetBankingConnectionQueryVariablesType
  >({
    query: getBankingConnectionQuery,
    variables: payload,
  });
};

export const getCompanyBankingConnections = async (
  payload: GetCompanyBankingConnectionQueryVariablesType
) => {
  return proxy<
    GetCompanyBankingConnectionQueryType,
    GetCompanyBankingConnectionQueryVariablesType
  >({
    query: getCompanyBankingConnectionsQuery,
    variables: payload,
  });
};
export const getCompanyErpConnections = async (
  payload: GetCompanyErpConnectionQueryVariablesType
) => {
  return proxy<
    GetCompanyErpConnectionQueryType,
    GetCompanyErpConnectionQueryVariablesType
  >({
    query: getCompanyErpConnectionsQuery,
    variables: payload,
  });
};

export const startErpConnection = async ({
  organizationNumber,
  country,
}: {
  organizationNumber: string;
  country: string;
}) =>
  save<{ link: string; uid: string }>({
    url: CREATE_ERP_CONNECTION,
    method: REQUEST_METHOD.POST,
    data: {
      orgnr: organizationNumber,
      country,
    },
  });

export const refreshErpConnection = async ({
  organizationNumber,
  country,
}: {
  organizationNumber: string;
  country: string;
}) =>
  save<ErpConnectionResponse>({
    url: REFRESH_ERP_CONNECTION,
    method: REQUEST_METHOD.POST,
    data: {
      orgnr: organizationNumber,
      country,
    },
  });

export const disconnectErpConnection = async ({
  organizationNumber,
  country,
}: {
  organizationNumber: string;
  country: string;
}) =>
  save<ErpConnectionResponse>({
    url: DISCONNECT_ERP_CONNECTION,
    method: REQUEST_METHOD.POST,
    data: {
      orgnr: organizationNumber,
      country,
    },
  });

export const startBankingConnection = async ({
  organizationNumber,
  country,
}: {
  organizationNumber: string;
  country: string;
}) =>
  save<{ link: string; uid: string }>({
    url: START_BANKING_CONNECTION,
    method: REQUEST_METHOD.POST,
    data: {
      orgnr: organizationNumber,
      country,
    },
  });

export const refreshBankingConnection = async ({
  organizationNumber,
  country,
}: {
  organizationNumber: string;
  country: string;
}) =>
  save<BankingConnectionResponse>({
    url: REFRESH_BANKING_CONNECTION,
    method: REQUEST_METHOD.POST,
    data: {
      orgnr: organizationNumber,
      country,
    },
  });
export const disconnectBankingConnection = async ({
  organizationNumber,
  country,
}: {
  organizationNumber: string;
  country: string;
}) =>
  save<BankingConnectionResponse>({
    url: DISCONNECT_BANKING_CONNECTION,
    method: REQUEST_METHOD.POST,
    data: {
      orgnr: organizationNumber,
      country,
    },
  });

export const getCreditFees = (companyId: number) =>
  save<CompanyFees>({
    url: COMPANY_FEES(companyId),
    method: REQUEST_METHOD.GET,
  });

export const getFees = (queryOptions?: QueryOptions) => {
  return proxy<GetFeesQueryType>({
    query: getFeesQuery(queryOptions),
  });
};

export const getCompanyLimitExpirationDate = async (companyId: number) => {
  return proxy<GetCompanyLimitExpirationDateType, { companyId: number }>({
    query: getCompanyLimitExpirationDateQuery,
    variables: { companyId },
  });
};

export const getLimitDeactivationReason = async (companyId: number) => {
  return proxy({
    query: getLimitDeactivationReasonQuery,
    variables: { companyId },
  });
};
// operator queries

export const impersonateUser = async (params: ImpersonateUserPayload) =>
  save<ImpersonateUserResponse>({
    url: IMPERSONATE_USER,
    data: params,
    method: REQUEST_METHOD.POST,
  });

export const companyBankAccounts = async (companyId: number) => {
  return proxy<GetCompanyBankAccountsQueryType, { companyId: number }>({
    query: getCompanyBankAccountsQuery,
    variables: { companyId },
  });
};

export const deactivateCompanyBankAccount = async (
  payload: DeactivateCompanyBankAccountMutationVariablesType
) => {
  return proxy<
    DeactivateCompanyBankAccountMutationType,
    DeactivateCompanyBankAccountMutationVariablesType
  >({
    mutation: deactivateCompanyBankAccountMutation,
    variables: payload,
  });
};

export const getFeatureFlags = async ({
  userId,
  companyId,
  country,
}: {
  userId: string;
  companyId: string;
  country: string;
}) => {
  return proxy<GetAllFeatureFlagsQueryType, GetAllFeatureFlagsQueryVariables>({
    query: getAllFeatureFlags,
    variables: { userId, companyId, country },
  });
};

export const getCompanyVouchers = async (queryOptions?: QueryOptions) => {
  return proxy<GetCompanyVouchersType, undefined>({
    query: getCompanyVouchersQuery(queryOptions),
  });
};

export const getCreditsByInvoiceRef = async (invoiceRef: number) => {
  return proxy<GetInvoiceCreditsTypes, GetInvoiceCreditsQueryVariables>({
    query: getCreditAmountByInvoiceQuery,
    variables: { invoiceSystemRef: invoiceRef },
  });
};

export const getOrderVouchers = async (orderId: number) => {
  return proxy<GetOrderVouchersType, GetOrderVouchersQueryVariables>({
    query: getOrderVouchersQuery,
    variables: { orderId },
  });
};

export const getCompanyCredits = async (limit: number, page: number) =>
  save<GetCompanyCreditsType>({
    url: GET_ALL_CREDITS(limit, page),
    method: REQUEST_METHOD.GET,
  });

export const getCompanyAvailableCreditsAmount = async () =>
  save<{ available_credits: number }>({
    url: GET_AVAILABLE_CREDITS_AMOUNT,
    method: REQUEST_METHOD.GET,
  });

export const getActiveBankAccountById = async (
  payload: GetActiveCompanyBankAccountByIdQueryVariablesType
) => {
  return proxy<
    GetActiveCompanyBankAccountByIdQueryType,
    GetActiveCompanyBankAccountByIdQueryVariablesType
  >({
    query: getActiveCompanyBankAccountByIdQuery,
    variables: { companyId: payload.companyId, bankId: payload.bankId },
  });
};
export const getBankAccountById = async (
  payload: GetCompanyBankAccountByIdQueryVariablesType
) => {
  return proxy<
    GetCompanyBankAccountByIdQueryType,
    GetCompanyBankAccountByIdQueryVariablesType
  >({
    query: getCompanyBankAccountByIdQuery,
    variables: { companyId: payload.companyId, bankId: payload.bankId },
  });
};
export const validateKycData = async ({
  companyId,
  data,
}: {
  companyId: number;
  data: Partial<Pick<KycData, 'company_signers'>>;
}) =>
  save<KycValidationResponse>({
    url: VALIDATE_KYC_DATA(companyId),
    method: REQUEST_METHOD.POST,
    data,
  });

export const getImpersonateSearchResults = async (
  queryOptions?: QueryOptions
) =>
  proxy<GetImpersonateSearchResultsQueryType>({
    query: getImpersonateSearchResultsQuery(queryOptions),
  });
